import { useEffect, useRef, useState, type RefObject } from "react";

import type { Cluster } from "./ClusterCard.js";
import { pct } from "./PtzPanel.js";
import { useI18n } from "../i18n.js";
import type { SmoothPtz } from "../ptz-geometry.js";

/**
 * Dove punta il laser dentro il fotogramma, portato da `FocusMode.qml`.
 *
 * Laser e camera stanno sullo stesso gruppo PTZ: ruotano insieme, quindi il
 * punto NON dipende da dove e' girata la testa — dipende solo dallo zoom e da
 * quanto e' lontano il bersaglio. Lo spot cade a un angolo di parallasse
 * `atan(base/distanza)` dall'asse della camera, che sul fotogramma vale quel
 * angolo diviso il campo visivo:
 *
 *     frazione = boresight + segno * atan(base/distanza) / FOV(zoom)
 *
 * con la frazione in [-0.5 bordo .. 0 centro .. +0.5 bordo].
 *
 * La distanza il laser non la dichiara (arriva a 50 m), quindi e' un cursore
 * che l'operatore muove: e' l'unico numero di questa schermata che non venga
 * da un apparato, ed e' dichiarato tale sotto al cursore stesso.
 *
 * Sul rig a ottica fissa (BIK-INFORM: Hikvision senza zoom) il campo visivo e'
 * costante. Interpolare verso un teleobiettivo che non esiste porterebbe il
 * reticolo nel punto sbagliato — con i valori di riferimento, da 55 a 39 gradi
 * di campo.
 */
export function LaserMarker({ cluster, ptz }: { cluster: Cluster; ptz: RefObject<SmoothPtz> }) {
  const { t } = useI18n();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const reticleRef = useRef<HTMLDivElement | null>(null);
  const edgeRef = useRef<HTMLDivElement | null>(null);
  const glyphRef = useRef<HTMLSpanElement | null>(null);

  const [dist, setDist] = useState(() => Number(cluster.laserTargetDist) || 25);
  useEffect(() => setDist(Number(cluster.laserTargetDist) || 25), [cluster.laserTargetDist]);

  const baseB = Number(cluster.laserBaseB) || 0.5; // m, scostamento laterale laser-camera
  const vOff = Number(cluster.laserVOff) || 0; // m, scostamento verticale
  const fovWide = Number(cluster.cameraFovWide) || 55; // gradi a zoom 0
  const fovTele = Number(cluster.cameraFovTele) || 2.3; // gradi a zoom massimo
  const boreH = Number(cluster.laserBoresightH) || 0;
  const boreV = Number(cluster.laserBoresightV) || 0;
  // Laser a sinistra della camera -> spot nella meta' sinistra del fotogramma.
  const sideSign = Number(cluster.laserSideSign) || -1;
  const hasZoomAxis = (cluster.ptzModel ?? "") !== "bik-inform";

  // Il calcolo gira a ogni fotogramma e scrive direttamente sullo stile: lo
  // zoom e' interpolato fuori dallo stato di React, e farlo passare di li'
  // ridisegnerebbe tutta la schermata per spostare un reticolo.
  const geom = useRef({ baseB, vOff, fovWide, fovTele, boreH, boreV, sideSign, hasZoomAxis, dist });
  geom.current = { baseB, vOff, fovWide, fovTele, boreH, boreV, sideSign, hasZoomAxis, dist };

  useEffect(() => {
    const box = boxRef.current;
    const reticle = reticleRef.current;
    const edge = edgeRef.current;
    const glyph = glyphRef.current;
    if (!box || !reticle || !edge || !glyph) return;

    let raf = 0;
    const frame = () => {
      const g = geom.current;
      const bw = box.clientWidth;
      const bh = box.clientHeight;
      if (bw > 0 && bh > 0) {
        const zoom01 = g.hasZoomAxis
          ? Math.max(0, Math.min(1, (ptz.current?.zoom ?? 0) / 100))
          : 0;
        const fovH = g.fovWide + (g.fovTele - g.fovWide) * zoom01;
        // Proporzioni del fotogramma: quelle vere se il decodificatore le ha
        // gia' dette, 16:9 finche' non arriva il primo fotogramma.
        const aspect = aspectOf(box) ?? 16 / 9;
        const fovV = (2 * Math.atan(Math.tan((fovH * Math.PI) / 360) / aspect) * 180) / Math.PI;

        const d = Math.max(0.5, g.dist);
        const parH = (Math.atan(g.baseB / d) * 180) / Math.PI;
        const parV = (Math.atan(g.vOff / d) * 180) / Math.PI;
        const fracX = g.boreH + (g.sideSign * parH) / fovH;
        const fracY = g.boreV + parV / fovV;

        // Il fotogramma dentro il riquadro: "contain", come il video.
        const boxAspect = bw / bh;
        const fitW = aspect > boxAspect ? bw : bh * aspect;
        const fitH = aspect > boxAspect ? bw / aspect : bh;
        const fitX = (bw - fitW) / 2;
        const fitY = (bh - fitH) / 2;

        const off = Math.abs(fracX) > 0.5 || Math.abs(fracY) > 0.5;
        reticle.style.display = off ? "none" : "block";
        edge.style.display = off ? "flex" : "none";

        if (off) {
          // Fuori inquadratura: succede davvero, a forte zoom su un bersaglio
          // vicino. Una pillola sul bordo verso cui punta il laser dice dove
          // guardare, invece di lasciare il reticolo incollato a un angolo.
          const cx = Math.max(-0.5, Math.min(0.5, fracX));
          const cy = Math.max(-0.5, Math.min(0.5, fracY));
          const ew = edge.offsetWidth;
          const eh = edge.offsetHeight;
          const x = Math.max(
            fitX + 4,
            Math.min(fitX + fitW - ew - 4, fitX + (0.5 + cx) * fitW - ew / 2),
          );
          const y = Math.max(
            fitY + 4,
            Math.min(fitY + fitH - eh - 4, fitY + (0.5 + cy) * fitH - eh / 2),
          );
          edge.style.transform = `translate(${x}px, ${y}px)`;
          glyph.textContent =
            Math.abs(fracX) >= Math.abs(fracY)
              ? fracX < 0
                ? "◄"
                : "►"
              : fracY < 0
                ? "▲"
                : "▼";
        } else {
          const x = fitX + (0.5 + fracX) * fitW;
          const y = fitY + (0.5 + fracY) * fitH;
          reticle.style.transform = `translate(${x}px, ${y}px)`;
        }
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [ptz]);

  return (
    <>
      <div className="lasermk" ref={boxRef}>
        <div className="lasermk__reticle" ref={reticleRef}>
          <i className="lasermk__ring" />
          <i className="lasermk__dot" />
          <i className="lasermk__tick lasermk__tick--t" />
          <i className="lasermk__tick lasermk__tick--b" />
          <i className="lasermk__tick lasermk__tick--l" />
          <i className="lasermk__tick lasermk__tick--r" />
          <span className="lasermk__label">LASER</span>
        </div>
        <div className="lasermk__edge" ref={edgeRef}>
          <span className="lasermk__glyph" ref={glyphRef} />
          <span>{t("focus.laserOffScreen")}</span>
        </div>
      </div>

      <div className="laserdist">
        <div className="laserdist__row">
          <span>{t("focus.laserDistance")}</span>
          <b>{Math.round(dist)} m</b>
        </div>
        <input
          type="range"
          min={1}
          max={50}
          step={1}
          value={dist}
          style={pct(dist, 1, 50)}
          onChange={(e) => setDist(Number(e.target.value))}
        />
      </div>
    </>
  );
}

/** Le proporzioni del video che sta nello stesso riquadro. Si leggono
 *  dall'elemento invece di farsele passare: il marker vive dentro il player,
 *  e il valore cambia da solo quando cambia il flusso. */
function aspectOf(box: HTMLElement): number | undefined {
  const video = box.parentElement?.querySelector("video");
  if (!video) return undefined;
  const w = video.videoWidth;
  const h = video.videoHeight;
  return w > 0 && h > 0 ? w / h : undefined;
}
